<template>
  <div>
    <div class="payment-selection-body">
      <div v-if="acceptNetTerms" class="payment-selection">
        <p style="margin: 0.5rem 0; font-weight: bold">
          To checkout using net terms, please select the box below and enter the
          payment method
        </p>
        <input
          id="net_terms_checkbox"
          type="checkbox"
          name="net_terms"
          :checked="netTermsPayments"
          @change="handleNetTermsChange"
        />
        <label for="net_terms_checkbox">Net Terms Payments</label>
      </div>
      <div v-if="!netTermsPayments">
        <div v-for="paymentMethod in paymentMethods" :key="paymentMethod.type">
          <div
            v-if="paymentMethod.cards.length > 0 && paymentMethod.access"
            class="payment-selection"
          >
            <p style="margin: 0.5rem 0; font-weight: bold">
              {{ paymentMethod.title }}
            </p>
            <div
              v-for="(card, i) in paymentMethod.cards"
              :key="i"
              :class="
                card.id === selectedId
                  ? 'payment-selection-row payment-selection-row-selected'
                  : 'payment-selection-row'
              "
              @click="handleClick(card.id)"
            >
              <input
                v-model="selectedId"
                type="radio"
                :value="card.id"
                name="payment_method"
              />
              <checkout-payment-method-view
                :card="card"
              ></checkout-payment-method-view>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!netTermsPayments" style="padding: 1rem; text-align: left">
        <button
          class="btn btn-sm btn-secondary"
          style="margin-left: 3rem; margin-top: 1rem"
          @click="handleAddPaymentClick"
        >
          <i class="fa fa-plus"></i> Add new payment method
        </button>
      </div>
    </div>
    <div style="text-align: right; padding: 1.5rem; background-color: #eeeeee">
      <button class="btn btn-primary" @click="handleSubmit">Update</button>
    </div>
  </div>
</template>

<script>
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import CheckoutPaymentMethodView from "./CheckoutPaymentMethodView.vue";
import store from "../../../../store/stores";

export default {
  store,
  components: { CheckoutPaymentMethodView },
  data() {
    return {
      selectedId: null,
      acceptNetTermsPayments: false,
      addPaymentMethodError: false,
      paymentMethods: [
        {
          type: "bank_acct",
          title: "Bank accounts",
          access: null,
          cards: [],
        },
        {
          type: "ach_credit_transfer",
          title: "Bank Transfers",
          access: null,
          cards: [],
        },
        {
          type: "bank_transfer",
          title: "Virtual Bank",
          access: null,
          cards: [],
        },
        {
          type: "credit_card",
          title: "Credit and debit cards",
          access: 1,
          cards: [],
        },
        {
          type: "wallet",
          title: "Cloud wallet",
          access: 1,
          cards: [],
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      selectedBillingAddress: "checkout/billingAddress",
      selectedPaymentMethod: "checkout/paymentMethod",
      cards: "checkout/paymentMethods",
      userAchAccess: "checkout/userAchAccess",
      acceptNetTerms: "checkout/acceptNetTerms",
      netTermsPayments: "checkout/netTermsPayments",
      userAchCreditAccess: "checkout/userAchCreditAccess",
      userStripePaymentIntentAccess: "checkout/userStripePaymentIntentAccess",
    }),
  },
  beforeMount() {
    if (this.selectedPaymentMethod) {
      this.selectedId = this.selectedPaymentMethod.id;
    }
  },
  mounted() {
    this.acceptNetTermsPayments = this.netTermsPayments;
    this.paymentMethods.forEach((payments) => {
      let access = 1;
      const { type } = payments;

      // eslint-disable-next-line no-shadow
      // eslint-disable-next-line prettier/prettier
      const sameTypePayment = this.paymentMethods.find((payment) => payment.type === type);

      // Filter the cards based on the card type
      sameTypePayment.cards = this.cards.filter(
        (card) => card.account_type === type
      );

      // Logic to determine the value of 'access' based on the card type
      if (type === "ach_credit_transfer" || type === "bank_transfer") {
        access = this.userAchCreditAccess;
      }
      if (type === "bank_transfer") {
        access = this.userStripePaymentIntentAccess;
      }
      if (type === "bank_acct") {
        access = this.userAchAccess;
      }

      sameTypePayment.access = access;

      // New logic to show/hide ach_credit_transfer and bank_transfer based on userStripePaymentIntentAccess
      if (this.userStripePaymentIntentAccess) {
        // If access is true, hide 'ach_credit_transfer' and show bank_transfer
        if (type === "ach_credit_transfer") {
          sameTypePayment.access = null; // Disable access for ach_credit_transfer
        }
        if (type === "bank_transfer") {
          sameTypePayment.access = access; // Show bank_transfer
        }
      } else if (type === "ach_credit_transfer") {
        // If access is false, show ach_credit_transfer
        sameTypePayment.access = access; // Show ach_credit_transfer
      }
    });

    if (
      this.cards.some((card) => card.account_type === "ach_credit_transfer")
    ) {
      this.$store.dispatch("checkout/updateHasUserAchCredit", true);
    }

    if (this.cards.some((card) => card.account_type === "bank_transfer")) {
      this.$store.dispatch("checkout/updateHasUserBankTransfer", true);
    }
  },
  methods: {
    handleNetTermsChange(event) {
      this.acceptNetTermsPayments = event.target.checked;
      this.$store.commit(
        "checkout/SET_NET_TERMS_PAYMENTS",
        this.acceptNetTermsPayments
      );
    },
    handleClick(id) {
      this.selectedId = id;
    },
    handleSubmit() {
      this.$store.commit("checkout/SET_PAYMENT_METHOD_ID", this.selectedId);
      this.$store.commit(
        "checkout/SET_NET_TERMS_PAYMENTS",
        this.acceptNetTermsPayments
      );
      this.$parent.$emit("handleClose");
      document
        .querySelector("#checkout-panel")
        .scrollIntoView({ behavior: "smooth" });
    },
    handleAddPaymentClick() {
      if (!this.selectedBillingAddress) {
        swal.fire({
          title: "Oops..",
          type: "error",
          html:
            "<p style='font-size: small'>Please select a billing address " +
            "above to associate with your new payment method before " +
            "attempting to add a new payment method.</p>",
        });
      } else {
        this.$emit("addPayment");
        document
          .querySelector("#billing_address")
          .scrollIntoView({ behavior: "smooth" });
      }
    },
  },
};
</script>

<style>
.payment-selection-body {
  padding: 2rem;
}

.payment-selection {
  text-align: left;
}

.payment-selection span {
  font-weight: bold;
}

.payment-selection-row {
  display: flex;
  flex-direction: row;
  padding: 1.5rem 1.5rem 0.5rem;
  border: 1px solid #ffffff;
  border-radius: 1rem;
  transition: background-color 0.2s, border 0.2s;
  cursor: pointer;
}

.payment-selection-row-selected {
  background-color: #f5e1cc;
  border: 1px solid #d16c00;
}

.payment-selection-row input {
  margin-right: 2rem;
}

@media (min-width: 768px) {
  .payment-selection-body {
    padding: 2rem 3rem;
  }
}
</style>
